import { AxiosRequestConfig } from 'axios';
import { callFes } from '..';
import {
  CreateCreditApplicationRequest,
  CreateCreditApplicationResponse,
  CreditApplicationDownloadDocumentsResponse,
  GetCreditApplicationStipulationsDocumentsResponse,
  GetCreditApplicationStipulationsResponse,
  StipulationDocumentUploadResponse,
} from './types';

export const getCreditApplication = async (
  creditApplicationId: string,
  config?: AxiosRequestConfig
): Promise<CreditIntegration.Schemas.CreditApplicationResponse> => {
  return callFes.getMiles(`credit-applications/${creditApplicationId}`, false, config);
};

export const createCreditApplication = async (
  quoteId: string,
  payload: CreateCreditApplicationRequest
): Promise<CreateCreditApplicationResponse> => {
  return callFes.postMiles(`quotes/${quoteId}/credit-applications/create`, payload);
};

export const getCreditApplicationsList = async (
  searchQuery: string
): Promise<Required<CreditIntegration.Schemas.GetListCreditApplicationsResponse>> => {
  return callFes.getMiles(`credit-applications?searchQuery=${searchQuery}`);
};

export const getCreditApplicationFilteredList = async (params?: {
  offerIds?: string;
  creditApplicationStatus?: string;
  milesBrokerCompanyId?: string;
  milesCustomerId?: string;
  milesSalesPersonId?: string;
  count?: string;
  offset?: string;
  searchQuery?: string;
  includeConversationInfo?: string;
}): Promise<CreditIntegration.Schemas.GetListCreditApplicationsResponse> => {
  const queryParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) queryParams.append(key, value);
  });

  const queryString = queryParams.toString();
  const url = `credit-applications${queryString ? `?${queryString}` : ''}`;

  return callFes.getMiles(url);
};

export const getCreditApplicationCustomerStipulations = async (
  quoteId: string,
  creditApplicationId: string
): Promise<GetCreditApplicationStipulationsResponse> => {
  return callFes.getMiles(`quotes/${quoteId}/credit-applications/${creditApplicationId}/stipulations`);
};

export const getCreditApplicationCustomerStipulationsDocuments = async (
  quoteId: string,
  creditApplicationId: string
): Promise<GetCreditApplicationStipulationsDocumentsResponse> => {
  return callFes.getMiles(`quotes/${quoteId}/credit-applications/${creditApplicationId}/stipulations/documents`);
};

export const getCreditApplicationCustomerStipulationsSingleDocument = async (
  quoteId: string,
  creditApplicationId: string,
  stipulationId: string,
  documentId: string
): Promise<CreditApplicationDownloadDocumentsResponse> => {
  return callFes.getMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/stipulations/${stipulationId}/documents/${documentId}`
  );
};

export const deleteCreditApplicationStipulationDocument = async (
  quoteId: string,
  creditApplicationId: string,
  stipulationId: string,
  documentId: string
): Promise<undefined> => {
  return callFes.deleteMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/stipulations/${stipulationId}/documents/${documentId}`
  );
};

export const getCreditApplicationCustomerData = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string
): Promise<CreditIntegration.Schemas.GetApplicantDetailsCustomerData> => {
  return callFes.getMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}`
  );
};

export const patchCreditApplicationCustomerData = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string,
  payload: CreditIntegration.Schemas.UpdateCustomerDetailsRequest
): Promise<CreditIntegration.Schemas.GetApplicantDetailsCustomerData> => {
  return callFes.patchMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}`,
    payload
  );
};

export const uploadCreditApplicationStipulationDocument = async (
  quoteId: string,
  creditApplicationId: string,
  stipulationId: string,
  payload: Pick<MediaStorageService.Schemas.FilesResponse['file'], 'id' | 'mimeType'>
): Promise<StipulationDocumentUploadResponse> => {
  return callFes.postMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/stipulations/${stipulationId}/documents`,
    payload,
    false
  );
};

// CO_CUSTOMER STIPULATIONS

export const getCreditApplicationCoCustomerStipulations = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string
): Promise<GetCreditApplicationStipulationsResponse> => {
  return callFes.getMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}/stipulations`
  );
};

export const getCreditApplicationCoCustomerStipulationsDocuments = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string
): Promise<GetCreditApplicationStipulationsDocumentsResponse> => {
  return callFes.getMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}/stipulations/documents`
  );
};

export const getCreditApplicationCoCustomerStipulationsSingleDocument = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string,
  stipulationId: string,
  documentId: string
): Promise<CreditApplicationDownloadDocumentsResponse> => {
  return callFes.getMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}/stipulations/${stipulationId}/documents/${documentId}`
  );
};

export const deleteCreditApplicationCoCustomerStipulationDocument = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string,
  stipulationId: string,
  documentId: string
): Promise<undefined> => {
  return callFes.deleteMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}/stipulations/${stipulationId}/documents/${documentId}`
  );
};

export const uploadCreditApplicationCoCustomerStipulationDocument = async (
  quoteId: string,
  creditApplicationId: string,
  applicantCreditScoreId: string,
  stipulationId: string,
  payload: Pick<MediaStorageService.Schemas.FilesResponse['file'], 'id' | 'mimeType'>
): Promise<StipulationDocumentUploadResponse> => {
  return callFes.postMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/applicant-credit-scores/${applicantCreditScoreId}/stipulations/${stipulationId}/documents`,
    payload
  );
};

export const submitCreditApplication = async (
  quoteId: string,
  creditApplicationId: string,
  config?: AxiosRequestConfig
): Promise<CreditIntegration.Schemas.SubmitCreditApplicationResponse> => {
  return callFes.postMiles(
    `quotes/${quoteId}/credit-applications/${creditApplicationId}/submission`,
    undefined,
    false,
    config
  );
};
